import { useEffect, useState, ChangeEvent } from 'react';
import { useMutation } from '@apollo/client';
import { Grid, TextField } from '@mui/material';

import { ADD_AGENT, UPDATE_AGENT } from '../../graphql/agent';
import { FragmentType, useFragment } from '../../__generated__';
import { SatelliteAgentListItemFragment } from './SatelliteAgentListItem';
import ErrorView from '../../components/common/ErrorView';
import FormDialogContainer, {
  DialogBody,
} from '../../components/common/FormDialogContainer';

type EditSatelliteAgentContainerProps = {
  open: boolean;
  agent?: FragmentType<typeof SatelliteAgentListItemFragment>;
  onClose: () => void;
};

const EditSatelliteAgentContainer = (
  props: EditSatelliteAgentContainerProps
) => {
  const { open, onClose } = props;
  const agent =
    useFragment(SatelliteAgentListItemFragment, props.agent) || null;

  const [updateAgent, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_AGENT);
  const [addAgent, { loading: addLoading, error: addError }] =
    useMutation(ADD_AGENT);
  const [name, setName] = useState<string>(agent?.name || '');
  const [email, setEmail] = useState<string>(agent?.email || '');
  const [phone, setPhone] = useState<string>(agent?.phone || '');
  const [limit, setLimit] = useState<string>(agent?.limit?.toString() || '');
  const [formError, setFormError] = useState<string>();

  useEffect(() => {
    setName(agent?.name || '');
    setEmail(agent?.email || '');
    setPhone(agent?.phone || '');
    setLimit(agent?.limit?.toString() || '');
    setFormError('');
  }, [agent]);

  const handleStringChange = (
    element: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    updateCallBack: (value: string) => void
  ) => {
    const value = element.target.value;
    updateCallBack(value);
  };

  const handleCurrencyChange = (
    element: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    updateCallBack: (value: string) => void
  ) => {
    let value = element.target.value || '';
    if (value.length === 0) {
      value = '';
    } else {
      value = parseFloat(value).toString();
    }
    updateCallBack(value);
  };

  const allowSubmit = () => {
    const result = name && email && phone && limit && !loading;
    return Boolean(result);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    if (allowSubmit()) {
      const agentLimit = parseFloat(limit || '0');
      if (agent) {
        const result = await updateAgent({
          variables: {
            id: agent.id,
            name,
            email,
            phone,
            limit: agentLimit,
            role: 'satellite',
          },
        });
        if (result?.data?.updateAgent?.success) {
          onClose();
        } else {
          setFormError(result?.data.updateAgent?.message);
        }
      } else {
        const result = await addAgent({
          variables: {
            name,
            email,
            phone,
            limit: agentLimit,
            role: 'satellite',
          },
        });
        if (result?.data?.addAgent?.success) {
          onClose();
        } else {
          setFormError(result?.data.addAgent?.message);
        }
      }
    } else {
      setFormError(
        'There was an issue with the form submission. Please contact system admin.'
      );
    }
  };

  const loading = agent ? updateLoading : addLoading;
  const error = agent ? updateError : addError;
  const isEditMode = agent && agent.id;
  const errorMessage = formError || error?.message;
  return (
    <FormDialogContainer>
      <DialogBody
        open={open}
        loading={loading}
        disabled={!allowSubmit()}
        title={isEditMode ? 'Edit Satellite Agent' : 'New Satellite Agent'}
        onClose={onClose}
        onSubmitAsync={handleSubmit}
      >
        <Grid container spacing={3} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              name="name"
              label="Full Name"
              value={name}
              onChange={(e) =>
                handleStringChange(e, (value: string) => setName(value))
              }
              type="text"
              fullWidth
              required
              variant="standard"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              id="email"
              name="email"
              label="Email"
              value={email}
              onChange={(e) =>
                handleStringChange(e, (value: string) => setEmail(value))
              }
              type="text"
              fullWidth
              required
              variant="standard"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              id="phone"
              name="phone"
              label="Phone Number"
              value={phone}
              onChange={(e) =>
                handleStringChange(e, (value: string) => setPhone(value))
              }
              type="text"
              fullWidth
              required
              variant="standard"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              id="limit"
              name="limit"
              label="Transfer Limit"
              value={limit}
              onChange={(e) =>
                handleCurrencyChange(e, (value: string) => setLimit(value))
              }
              type="number"
              fullWidth
              required
              variant="standard"
            />
          </Grid>
        </Grid>
        {errorMessage && <ErrorView message={errorMessage} />}
      </DialogBody>
    </FormDialogContainer>
  );
};

export default EditSatelliteAgentContainer;
